import { TPropsWithChildren } from "../types/util";
import React, {
  createContext,
  createRef,
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import WaveSurfer, { WaveSurferOptions } from "wavesurfer.js";
import RecordPlugin from "wavesurfer.js/dist/plugins/record";

type TWaveSurferRecorderContext = {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  wavesurfer: WaveSurfer | null;
  setWaveSurfer: Dispatch<SetStateAction<WaveSurfer | null>>;
  recorder: RecordPlugin | null;
  setRecorder: Dispatch<SetStateAction<RecordPlugin | null>>;
  height: number;
  setHeight: Dispatch<SetStateAction<number>>;
};

type TProps = TPropsWithChildren;

const WaveSurferRecorderContext = createContext<TWaveSurferRecorderContext>({
  containerRef: createRef(),
  wavesurfer: null,
  setWaveSurfer: () => {},
  recorder: null,
  setRecorder: () => {},
  height: 0,
  setHeight: () => {},
});

const WaveSurferRecorderProvider = (props: TProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [wavesurfer, setWaveSurfer] = useState<WaveSurfer | null>(null);
  const [recorder, setRecorder] = useState<RecordPlugin | null>(null);
  const [height, setHeight] = useState(58);

  // Create a canvas gradient
  const ctx = document.createElement("canvas").getContext("2d");
  const gradient = ctx!.createLinearGradient(0, 0, 0, 150);
  gradient.addColorStop(0, "rgb(244,59,71)");
  gradient.addColorStop(1, "rgb(69,58,148)");

  const options: Partial<WaveSurferOptions> = {
    waveColor: gradient,
    progressColor: "transparent",
    height,
    normalize: true,
    minPxPerSec: 2000,
  };

  // Initialize wavesurfer when the container mounts
  // or any of the props change
  useEffect(() => {
    if (!containerRef.current) return;

    const ws = WaveSurfer.create({
      ...options,
      container: containerRef.current,
    });

    setWaveSurfer(ws);
    setRecorder(ws.registerPlugin(RecordPlugin.create({ lineWidth: 2 })));

    return () => {
      ws?.destroy();
    };
  }, [containerRef]);

  return (
    <WaveSurferRecorderContext.Provider
      value={{
        containerRef,
        wavesurfer,
        setWaveSurfer,
        recorder,
        setRecorder,
        height,
        setHeight,
      }}
    >
      {props.children}
    </WaveSurferRecorderContext.Provider>
  );
};

const useWaveSurferRecorderContext = () =>
  useContext(WaveSurferRecorderContext);

export { WaveSurferRecorderProvider, useWaveSurferRecorderContext };
